import { generateSearchPath } from '@/components/organisms/SearchExperiences/generateSearchPath';
import { DESTINATION_THEME_CATEGORY_LABEL, normalizeWebPath, type AlgoliaExperienceHit, type AlgoliaFeatureHit, type AlgoliaTagHit } from '@/wabunka-src/features/Search/models/algoliaHit';

/**
 * Algolia hit から遷移先パスを生成する。
 *
 * Feature の遷移先は `wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured.ts`
 * の generateDetailPageLink と同じマッピングに揃える。
 */

export interface TagLink {
  path: string;
  query?: Record<string, string | undefined>;
}

export const buildTagPath = (hit: AlgoliaTagHit): TagLink => {
  const { webPath, tagGroupType } = hit.tag;
  if (tagGroupType === 'destination') {
    return { path: `/${webPath}/` };
  }
  if (tagGroupType === 'preference') {
    // Filter モーダル (SearchExperiences) と同じ generateSearchPath ロジックで遷移先を組み立てる
    const { path, query } = generateSearchPath(undefined, undefined, undefined, undefined, webPath);
    return { path, query };
  }
  return { path: `/japan/${webPath}/` };
};

/**
 * 体験ページへの遷移パス。Algolia の queryID / objectID を引き継いで予約完了時の
 * purchasedObjectIDsAfterSearch に利用できるようにクエリパラメータに付与する。
 */
export const buildExperienceLocation = (hit: AlgoliaExperienceHit): { path: string; query: Record<string, string | undefined> } => {
  return {
    path: `/experiences/${hit.experience.slug}/`,
    query: {
      queryID: hit.__queryID,
      algoliaObjectID: hit.objectID,
    },
  };
};

/**
 * Destination × Theme ページ（/{destination}/{theme}）への遷移パス。
 * feature.webPath（destination-theme のみ非空）を、同期元の表記揺れ（先頭/末尾スラッシュ）を吸収し
 * 先頭スラッシュ1つ・末尾スラッシュありに正規化する（URL の正規形 / ADR-146）。webPath 未設定なら '/' にフォールバック。
 */
export const buildDestinationThemePath = (hit: AlgoliaFeatureHit): string => {
  const normalized = normalizeWebPath(hit.feature.webPath);
  return normalized === '' ? '/' : `/${normalized}/`;
};

export const buildFeaturePath = (hit: AlgoliaFeatureHit): string => {
  switch (hit.feature.categoryLabel) {
    case 'destination':
      return `/${hit.feature.slug}/`;
    case 'itinerary':
      return `/itineraries/${hit.feature.slug}/`;
    case 'theme':
      return `/japan/${hit.feature.slug}/`;
    // area / category は廃止予定の `/areas/{id}/` `/themes/{id}/` にしか遷移先がないため default の '/' に落とす（JCAT-6198）
    case DESTINATION_THEME_CATEGORY_LABEL:
      // Destination × Theme ページは feature.webPath（/{destination}/{theme}）へ（JCAT-5557）
      return buildDestinationThemePath(hit);
    default:
      return '/';
  }
};
